var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Box, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { FlowNodeTypes, } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { QueuePicker } from 'components/shared/queuePicker';
import { Duration } from 'fable/duration';
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import styles from './queueModal.module.scss';
const QUEUE_FIELD = 'queue';
const QUEUE_TIMEOUT_FIELD = 'queue_timeout';
export const QueueModal = ({ node, nodeLabel, replaceNode, triggerClose, }) => {
    const { payload } = node;
    const [errors, setErrors] = useState({});
    const [{ queue, queue_timeout }, setState] = useState(Object.assign({ queue: null }, payload));
    const isChanged = useIsChanged([payload === null || payload === void 0 ? void 0 : payload.queue, queue], [payload === null || payload === void 0 ? void 0 : payload.queue_timeout, queue_timeout]);
    const validate = useCallback(() => {
        const errors = {};
        if (!(queue === null || queue === void 0 ? void 0 : queue.value)) {
            errors[QUEUE_FIELD] = 'Selecting a queue is required';
        }
        if (queue_timeout && !queue_timeout.timeout_in_seconds) {
            errors[QUEUE_TIMEOUT_FIELD] = 'Please set a timeout value';
        }
        setErrors(errors);
        return Object.keys(errors).length === 0;
    }, [queue, queue_timeout]);
    const handleSaveClick = useCallback(() => {
        if (!validate()) {
            return;
        }
        const newPayload = Object.assign(Object.assign({}, node.payload), { queue: queue, queue_timeout });
        if (!queue_timeout) {
            delete newPayload.queue_timeout;
        }
        const nodeOptions = Object.assign(Object.assign({}, node), { badges: [], childIds: [], icon: '', isAction: false, isEditable: true, isRemovable: true, nodeType: FlowNodeTypes.QUEUE, overlay: [], payload: newPayload, text: '' });
        replaceNode(nodeOptions);
        triggerClose();
    }, [node, replaceNode, queue, queue_timeout, triggerClose, validate]);
    const handleQueueChange = useCallback((queue) => {
        if (!queue) {
            return;
        }
        setState((state) => (Object.assign(Object.assign({}, state), { queue: {
                value: queue.id,
                label: queue.name,
            } })));
    }, []);
    const handleToggleTimeout = useCallback((isOpen) => {
        setState((_a) => {
            var { queue_timeout } = _a, state = __rest(_a, ["queue_timeout"]);
            if (isOpen) {
                return Object.assign(Object.assign({}, state), { queue_timeout: {
                        timeout_in_seconds: 0,
                        timeout_node_id: (queue_timeout === null || queue_timeout === void 0 ? void 0 : queue_timeout.timeout_node_id) || uuid(),
                    } });
            }
            return state;
        });
    }, []);
    const handleTimeoutChange = useCallback((timeoutInSeconds) => {
        setState((state) => {
            var _a;
            return (Object.assign(Object.assign({}, state), { queue_timeout: {
                    timeout_in_seconds: timeoutInSeconds,
                    timeout_node_id: ((_a = state.queue_timeout) === null || _a === void 0 ? void 0 : _a.timeout_node_id) || uuid(),
                } }));
        });
    }, []);
    return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({ title: nodeLabel, onClose: triggerClose }, { children: _jsxs("div", Object.assign({ className: styles.description }, { children: ["The queue is where you\u2019d like your incoming conversations to end up based on the queues you\u2019ve set up. Haven\u2019t made any custom queues yet?", ' ', "Go to Settings > Queues > Add queue"] })) })), _jsxs("form", Object.assign({ onSubmit: handleSaveClick }, { children: [_jsxs(ModalBody, { children: [_jsx(QueuePicker, { required: true, selectedQueue: queue, label: "Select queue", placeholder: "No queue selected", meta: { error: errors[QUEUE_FIELD] }, input: {
                                    name: 'select-queue',
                                    onChange: handleQueueChange,
                                } }), _jsx(ToggleBox, Object.assign({ isOpen: !!queue_timeout, headComponent: ToggleBoxHead, title: "Timeout", input: {
                                    id: 'timeout',
                                    name: 'timeout',
                                    value: 'timeout',
                                }, helpIcon: {
                                    title: 'Timeout',
                                    text: 'Select how many seconds the customer should wait in line before a new action is triggered.',
                                }, onChange: handleToggleTimeout }, { children: _jsxs(ToggleBoxBody, { children: [_jsx(Text.Paragraph, Object.assign({ className: styles.timeoutDescription, subdued: true }, { children: "Select how long the customer should wait before a new action is triggered." })), _jsx(Duration, { duration: queue_timeout === null || queue_timeout === void 0 ? void 0 : queue_timeout.timeout_in_seconds, label: "Set a timeout", meta: { error: errors[QUEUE_TIMEOUT_FIELD] }, onChange: handleTimeoutChange })] }) }))] }), _jsx(ModalFoot, { confirmOnClick: handleSaveClick, confirmName: getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged }, cancelOnClick: triggerClose })] }))] })));
};
